import { ApiError, type MessageKey } from "@tovi/shared";

/**
 * What to show a person when a request was refused.
 *
 * The API answers in three widths and this picks the widest one available:
 *
 *   1. a **reason**, which the pack turns into a sentence in the reader's own
 *      language — `error.brandInUse` → "វត្ថុធាតុដើមប្រើម៉ាកនេះ។";
 *   2. the server's **message**, which is specific and English. Nearly every
 *      throw stops here, deliberately: an operator or a developer reading
 *      "This transfer names no destination." is better served by that than by
 *      a generic sentence in any language;
 *   3. the caller's **fallback**, for the answers that carry no message at all
 *      — a network failure, or an HTTP/2 response whose `statusText` is empty.
 *
 * It lives in `@tovi/ui` beside `I18nProvider` for the reason the provider
 * itself does: the till may not import `@tovi/app-core` and the diner app will
 * not, so `@tovi/ui` is the only shelf all four apps reach.
 *
 * `t` is passed rather than read from a hook so this stays a function a render
 * body can call in a `catch`, which is where every one of its callers is.
 */
export function resolveErrorMessage(
  caught: unknown,
  t: (key: MessageKey) => string,
  fallback: MessageKey = "error.generic",
): string {
  if (caught instanceof ApiError) {
    // The union is what makes this typecheck: a reason with no key in the
    // English pack is a compile error here rather than an `error.someReason`
    // read off a screen.
    if (caught.reason) return t(`error.${caught.reason}`);
    if (caught.message) return caught.message;
  }
  return t(fallback);
}
